import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

/**
 * Billing context — one fetch per auth change, exposes:
 *   billing   → { plan, status, trial_days_left, locked, ... } | null
 *   refetch() → re-pull from /api/billing/me
 *
 * Also intercepts 402 responses globally so any write attempt against a
 * locked tenant immediately flips `locked=true` without waiting for the
 * next refetch. The BillingBanner subscribes to this and renders.
 */
const BillingContext = createContext({ billing: null, refetch: async () => {} });

export function BillingProvider({ children }) {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);

  const refetch = useCallback(async () => {
    if (!user) { setBilling(null); return; }
    try {
      const { data } = await api.get("/billing/me");
      setBilling(data);
    } catch {
      setBilling(null);
    }
  }, [user]);

  useEffect(() => { refetch(); }, [refetch]);

  // Install a one-time axios interceptor to catch 402s and flip local state.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 402) {
          setBilling((b) => (b ? { ...b, locked: true, lock_reason: err.response.data?.lock_reason || b.status } : b));
          // Only toast once per burst — sonner dedupes by id if we set one.
          toast.error(err.response.data?.detail || "Your free trial has ended.", {
            id: "billing-locked",
            duration: 5000,
            action: {
              label: "Upgrade",
              onClick: () => { window.location.href = "/billing"; },
            },
          });
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  return (
    <BillingContext.Provider value={{ billing, refetch }}>
      {children}
    </BillingContext.Provider>
  );
}

export const useBilling = () => useContext(BillingContext);
